"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import Compressor from "compressorjs";
import { FileUploader } from "@/components/ui/FileUploader";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function ImageCompressor() {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string>("");
  const [compressedPreview, setCompressedPreview] = useState<string>("");
  const [compressedFile, setCompressedFile] = useState<File | null>(null);
  const [quality, setQuality] = useState(0.8);
  const [isProcessing, setIsProcessing] = useState(false);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "image-compressor") ?? tools[0],
    locale
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setOriginalPreview(URL.createObjectURL(selectedFile));
      setCompressedPreview("");
      setCompressedFile(null);
    }
  };

  const compressImage = () => {
    if (!file) return;

    setIsProcessing(true);

    new Compressor(file, {
      quality,
      success(result) {
        const compressedBlob = result as Blob;
        const compressedFileObj = new File([compressedBlob], file.name, {
          type: file.type,
        });
        setCompressedFile(compressedFileObj);
        setCompressedPreview(URL.createObjectURL(compressedBlob));
        setIsProcessing(false);
      },
      error(err) {
        console.error("Compression error:", err.message);
        setIsProcessing(false);
      },
    });
  };

  const downloadCompressed = () => {
    if (!compressedFile) return;

    const url = URL.createObjectURL(compressedFile);
    const link = document.createElement("a");
    link.href = url;
    link.download = `compressed-${file?.name}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const compressionRatio = compressedFile && file
    ? Math.round(((file.size - compressedFile.size) / file.size) * 100)
    : 0;

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:information-variant" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      {!file ? (
        <FileUploader
          accept="image/*"
          onFilesSelected={(files) => {
            if (files && files[0]) {
               const syntheticEvent = {
                 target: {
                   files: files,
                 }
               } as unknown as React.ChangeEvent<HTMLInputElement>;
               handleFileChange(syntheticEvent);
            }
          }}
           className="rounded-[30px] bg-white border-dashed border-positivus-dark/20 p-8"
        >
          <div className="flex flex-col items-center gap-4 w-full">
            <div className="h-16 w-16 rounded-full bg-positivus-green/20 flex items-center justify-center">
              <Icon icon="mdi:cloud-upload" className="h-8 w-8 text-positivus-dark" />
            </div>
            <div>
              <p className="text-lg font-bold text-positivus-dark">{ui.clickToUploadImage}</p>
              <p className="text-sm text-gray-500 mt-1">{ui.supportsJpgPng}</p>
            </div>
          </div>
        </FileUploader>
      ) : (
        <div className="space-y-8">
           <div className="rounded-[30px] border-2 border-positivus-dark bg-white p-6 shadow-[0_5px_0_0_#191A23]">
             <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-positivus-green/20 flex items-center justify-center">
                  <Icon icon="mdi:image" className="h-6 w-6 text-positivus-dark" />
                </div>
                <div>
                   <p className="font-bold text-lg text-positivus-dark">{file.name}</p>
                   <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              <button
                onClick={() => setFile(null)}
                className="text-red-500 hover:text-red-700 font-medium text-sm border-2 border-transparent hover:border-red-100 rounded-[10px] px-3 py-1 transition-all"
              >
                {ui.remove}
              </button>
             </div>

             <div className="space-y-4">
                <div className="flex justify-between items-center">
                   <label className="text-lg font-medium text-positivus-dark">{ui.compressionQuality}</label>
                   <span className="font-bold text-positivus-green bg-positivus-green/10 px-3 py-1 rounded-[10px]">{Math.round(quality * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.1"
                  value={quality}
                  onChange={(e) => setQuality(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-positivus-dark"
                />
                 <div className="flex justify-between text-xs font-bold text-gray-400 mt-1">
                  <span>{ui.maximumCompression}</span>
                  <span>{ui.minimumCompression}</span>
                </div>
             </div>
           </div>

           <button
            onClick={compressImage}
            disabled={isProcessing}
            className="w-full rounded-[20px] bg-positivus-dark px-6 py-4 text-xl font-medium text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
               <div className="flex items-center justify-center gap-2">
                 <Icon icon="mdi:loading" className="h-6 w-6 animate-spin" />
                 <span>{ui.compressing}</span>
               </div>
            ) : ui.convert}
          </button>
        </div>
      )}

      {compressedFile && (
        <div className="rounded-[30px] border-2 border-positivus-dark bg-white p-8 shadow-[0_5px_0_0_#191A23] space-y-6">
           <div className="rounded-[20px] bg-green-50 border-2 border-green-200 p-5 flex items-center gap-4">
             <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
               <Icon icon="mdi:check-circle" className="h-6 w-6 text-green-600" />
             </div>
             <div>
               <h3 className="font-bold text-green-700 text-lg">{ui.compressionSuccessful}</h3>
               <p className="text-green-600 text-sm">{ui.readyForDownload}</p>
             </div>
           </div>

            <div className="grid grid-cols-2 gap-4">
               <div className="rounded-[20px] border-2 border-positivus-dark/10 bg-gray-50 p-5 text-center">
                 <p className="text-sm font-bold text-gray-500 mb-1 uppercase tracking-wider">{ui.originalSize}</p>
                 <p className="text-2xl font-black text-positivus-dark">{(file!.size / 1024).toFixed(1)} KB</p>
               </div>
               <div className="rounded-[20px] border-2 border-positivus-green/30 bg-positivus-green/10 p-5 text-center">
                 <p className="text-sm font-bold text-positivus-dark mb-1 uppercase tracking-wider text-opacity-70">{ui.compressedSize}</p>
                 <p className="text-2xl font-black text-positivus-green">{(compressedFile.size / 1024).toFixed(1)} KB</p>
               </div>
            </div>
            
            <div className="rounded-[20px] bg-gray-50 p-5">
               <div className="flex items-center justify-between mb-2">
                 <p className="font-bold text-positivus-dark">{ui.totalReduction}</p>
                 <p className="text-xl font-black text-positivus-green">-{compressionRatio}%</p>
               </div>
               <div className="h-4 w-full rounded-full bg-gray-200 overflow-hidden">
                 <div
                   className="h-full bg-positivus-green transition-all duration-1000 ease-out"
                   style={{ width: `${compressionRatio}%` }}
                 />
               </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-bold text-positivus-dark mb-3 pl-2">{ui.originalPreview}</p>
                <div className="rounded-[20px] border-2 border-positivus-dark/20 overflow-hidden bg-gray-100">
                  <img src={originalPreview} alt={ui.originalPreview} className="w-full h-auto object-cover" />
                </div>
              </div>
               <div>
                <p className="text-sm font-bold text-positivus-dark mb-3 pl-2">{ui.compressedPreview}</p>
                <div className="rounded-[20px] border-2 border-positivus-green overflow-hidden bg-gray-100 shadow-[0_4px_0_0_#191A23]">
                  <img src={compressedPreview} alt={ui.compressedPreview} className="w-full h-auto object-cover" />
                </div>
              </div>
            </div>

            <button
                onClick={downloadCompressed}
                className="w-full flex items-center justify-center gap-3 rounded-[20px] bg-positivus-green px-6 py-4 text-xl font-bold text-positivus-dark hover:bg-positivus-dark hover:text-white transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
              >
                <Icon icon={uiIcons.download} className="h-6 w-6" />
                {ui.downloadCompressedImage}
              </button>
        </div>
      )}
    </div>
  );
}
